import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import Card from 'components/card';
import { MdPeople, MdWork, MdAssignment, MdTrendingUp } from 'react-icons/md';
import { barChartOptionsDailyTraffic } from 'variables/charts';
import BarChart from 'components/charts/BarChart';
import headhrService from 'ApiService/Placement/headhrService';
import toast from 'react-hot-toast';
import RecentAssignments from 'pages/Admin/Dashboards/components/RecentAssignments';
import AnnouncementsCard from 'pages/Announcement/AnnouncementsCard';

const Dashboard = () => {
  const { state } = useContext(AuthContext);
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalHRs: 0,
    unassignedStudents: 0,
    assignedStudents: 0,
    regularHRs: 0,
    jrHRs: 0,
  });
  const [hrStats, setHrStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const selectedBranch = state?.selectedBranch;

  useEffect(() => {
    fetchDashboardStats();
  }, [selectedBranch]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await headhrService.getDashboardStats(selectedBranch);

      if (response.data.success) {
        setStats(response.data.data);
        setHrStats(response.data.data.hrStats || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error('Failed to fetch dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3">
      <AnnouncementsCard />
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <Card extra="!flex-row flex-grow items-center rounded-[20px]">
          <div className="ml-[18px] flex h-[90px] w-auto flex-row items-center">
            <div className="rounded-full bg-lightPrimary p-3 dark:bg-navy-700">
              <span className="flex items-center text-brand-500 dark:text-white">
                <MdPeople className="h-7 w-7" />
              </span>
            </div>
            <div className="h-50 ml-4 flex w-auto flex-col justify-center">
              <p className="font-dm text-sm font-medium text-gray-600">
                Total Students
              </p>
              <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                {loading ? '...' : stats.totalStudents}
              </h4>
            </div>
          </div>
        </Card>

        <Card extra="!flex-row flex-grow items-center rounded-[20px]">
          <div className="ml-[18px] flex h-[90px] w-auto flex-row items-center">
            <div className="rounded-full bg-lightPrimary p-3 dark:bg-navy-700">
              <span className="flex items-center text-brand-500 dark:text-white">
                <MdWork className="h-7 w-7" />
              </span>
            </div>
            <div className="h-50 ml-4 flex w-auto flex-col justify-center">
              <p className="font-dm text-sm font-medium text-gray-600">
                Total HRs
              </p>
              <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                {loading ? '...' : stats.totalHRs}
              </h4>
            </div>
          </div>
        </Card>

        <Card extra="!flex-row flex-grow items-center rounded-[20px]">
          <div className="ml-[18px] flex h-[90px] w-auto flex-row items-center">
            <div className="rounded-full bg-lightPrimary p-3 dark:bg-navy-700">
              <span className="flex items-center text-brand-500 dark:text-white">
                <MdAssignment className="h-7 w-7" />
              </span>
            </div>
            <div className="h-50 ml-4 flex w-auto flex-col justify-center">
              <p className="font-dm text-sm font-medium text-gray-600">
                Unassigned Students
              </p>
              <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                {loading ? '...' : stats.unassignedStudents}
              </h4>
            </div>
          </div>
        </Card>

        <Card extra="!flex-row flex-grow items-center rounded-[20px]">
          <div className="ml-[18px] flex h-[90px] w-auto flex-row items-center">
            <div className="rounded-full bg-lightPrimary p-3 dark:bg-navy-700">
              <span className="flex items-center text-brand-500 dark:text-white">
                <MdTrendingUp className="h-7 w-7" />
              </span>
            </div>
            <div className="h-50 ml-4 flex w-auto flex-col justify-center">
              <p className="font-dm text-sm font-medium text-gray-600">
                Assigned Students
              </p>
              <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                {loading ? '...' : stats.assignedStudents}
              </h4>
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-5 w-full">
        <RecentAssignments />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
        <Card extra="pb-7 p-[20px] h-full">
          <div className="flex flex-row justify-between">
            <div>
              <h4 className="text-lg font-bold text-navy-700 dark:text-white">
                HR Performance
              </h4>
              <p className="text-sm font-medium text-gray-600">
                Students assigned per HR
              </p>
            </div>
          </div>
          <div className="mt-5 h-[300px] w-full">
            {hrStats.length > 0 ? (
              <BarChart
                chartData={{
                  labels: hrStats.map((hr) => hr.name),
                  datasets: [
                    {
                      label: 'Assigned Students',
                      data: hrStats.map((hr) => hr.assignedStudents || 0),
                      backgroundColor: '#4318FF',
                    },
                  ],
                }}
                chartOptions={barChartOptionsDailyTraffic}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-gray-500">No data available</p>
              </div>
            )}
          </div>
        </Card>

        <Card extra="pb-7 p-[20px] h-full">
          <div className="flex flex-row justify-between">
            <div>
              <h4 className="text-lg font-bold text-navy-700 dark:text-white">
                Quick Actions
              </h4>
              <p className="text-sm font-medium text-gray-600">
                Manage HRs and assignments
              </p>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4">
            <button
              onClick={() => (window.location.href = '/headhr/manage-hrs')}
              className="flex items-center justify-between rounded-xl bg-brand-50 p-4 text-brand-500 transition-all hover:bg-brand-100 dark:bg-navy-700 dark:text-white"
            >
              <span className="font-medium">Manage HRs</span>
              <MdWork className="h-5 w-5" />
            </button>
            <button
              onClick={() => (window.location.href = '/headhr/assign-students')}
              className="flex items-center justify-between rounded-xl bg-brand-50 p-4 text-brand-500 transition-all hover:bg-brand-100 dark:bg-navy-700 dark:text-white"
            >
              <span className="font-medium">Assign Students</span>
              <MdAssignment className="h-5 w-5" />
            </button>
            <button
              onClick={() => (window.location.href = '/headhr/students')}
              className="flex items-center justify-between rounded-xl bg-brand-50 p-4 text-brand-500 transition-all hover:bg-brand-100 dark:bg-navy-700 dark:text-white"
            >
              <span className="font-medium">View All Students</span>
              <MdPeople className="h-5 w-5" />
            </button>
          </div>
        </Card>
      </div>

      <div className="mt-5">
        <Card extra="w-full pb-10 p-4">
          <div className="mb-4 flex items-center justify-between">
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              HR Team Summary
            </h4>
            <button
              onClick={() => (window.location.href = '/headhr/manage-hrs')}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-brand-600"
            >
              View All
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="pb-3 text-left text-sm font-medium text-gray-600">
                    Name
                  </th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-600">
                    Email
                  </th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-600">
                    Branches
                  </th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-600">
                    Assigned Students
                  </th>
                  <th className="pb-3 text-left text-sm font-medium text-gray-600">
                    Role
                  </th>
                </tr>
              </thead>
              <tbody>
                {hrStats.slice(0, 5).map((hr, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-3 text-sm font-medium text-navy-700 dark:text-white">
                      {hr.name}
                    </td>
                    <td className="py-3 text-sm text-gray-600">{hr.email}</td>
                    <td className="py-3 text-sm text-gray-600">
                      {hr.branches?.map((b) => b.name).join(', ') ||
                        'All Branches'}
                    </td>
                    <td className="py-3 text-sm text-gray-600">
                      {hr.assignedStudents || 0}
                    </td>
                    <td className="py-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          hr.hrRole === 'jrhr'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {hr.hrRole === 'jrhr' ? 'JR HR' : 'HR'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
